import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { Flag, Image, Link, useI18n } from '@packages/shared';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import { ProductPriceWrapper } from './ProductPriceWrapper';
import { PowerEfficiency } from './PowerEfficiency';
import { BasketAction, DeleteAction, SimilarProductAction } from '../Actions';
import { addToBasketStyleOverridesGalleryView } from '../Actions/Actions.styles';
import { buildGlycerinWishlistProduct } from '../Tracking/buildGlycerinObject';

interface WishlistItemsProps {
  data: FragmentType<typeof WishlistLineItemsFragmentFragmentDoc>;
}

export const GalleryViewItem: React.FC<WishlistItemsProps> = ({ data }) => {
  const fragmentData = getFragmentData(WishlistLineItemsFragmentFragmentDoc, data);
  const { language, localizeUrl } = useI18n();

  if (!fragmentData) return null;
  const { imageUrl, name, price, sku, isSoldOut, powerEfficiency, extra, deliveryMessage } =
    fragmentData;
  const glycerinProduct = buildGlycerinWishlistProduct(fragmentData);

  return (
    <div className="mb-1.5 mt-0 w-full break-inside-avoid md:mb-2">
      <div className="shadow-color-grey-dark bg-color-background-base relative flex w-full flex-col gap-1 overflow-visible rounded-[3px] p-1 pb-2 shadow-sm">
        <Link href={localizeUrl(`/p/${sku}`, language)} className="relative h-[170px] w-full">
          <Image priority unoptimized alt={name} src={imageUrl} fill className="object-contain" />
        </Link>
        <div className="flex flex-col gap-0.5">
          <h6 className="text-size-body3">{extra?.brand}</h6>
          <h5 className="text-size-body1 md:text-size-h5 col-span-2 col-start-2 row-span-1 max-h-[60px] overflow-hidden text-ellipsis font-semibold md:col-span-1">
            {name}
          </h5>
          {powerEfficiency && (
            <div className="col-start-2 row-start-4 row-end-5 md:row-start-4">
              <PowerEfficiency efficiency={powerEfficiency} />
            </div>
          )}
          <div className="md:pt-0">
            <ProductPriceWrapper isSoldOut={isSoldOut} price={price} alignmentOverride="left" />
          </div>
          <BasketAction
            data={fragmentData}
            galleryView
            overrideSx={addToBasketStyleOverridesGalleryView}
            glycerinProduct={glycerinProduct}
          />
          {isSoldOut && <SimilarProductAction data={fragmentData} />}
        </div>
        <div className="absolute right-0 top-0 h-auto rounded-lg p-1">
          <DeleteAction
            mode="wishlist"
            lineItemSku={sku}
            overrideButtonSx={{
              bgcolor: 'common.white',
              ':hover': {
                bgcolor: 'common.white',
              },
              boxShadow: 1,
              borderRadius: '50%',
              border: 'none',
              width: '1.5rem',
              height: '1.5rem',
              minHeight: '1.5rem',
              p: '0',
            }}
            galleryView
            glycerinProduct={glycerinProduct}
          />
        </div>
        {deliveryMessage === 'ausverkauft' && (
          <div className="absolute left-0 top-0 h-auto p-1">
            <Flag
              text={
                deliveryMessage.charAt(0).toUpperCase() + deliveryMessage.slice(1) // To make first letter uppercase.
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
